import {
  Button,
  Grid,
  List,
  Paper,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { SearchFilter } from 'components/ui/SearchFilter';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useEffect, useMemo, useState } from 'react';
import { useGet } from 'http/useApi';
import { rq } from 'http/apiRoutes';
import { useTranslation } from 'react-i18next';
import { IInvoiceViewModel } from './BillingConfig';
import { formatValue } from 'functions/formatValue';
import { SingleCompanyProductView } from 'components/ui/SingleCompanyProductView';
import { ISimpleTicketBillingViewModel } from 'Interfaces/ITicketConfig';
import SingleBillingTicketPostView from 'components/ui/SingleBillingTicketPostView';

export const Billed = () => {
  const { t } = useTranslation();
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [isLoadingSelectedInvoice, setIsLoadingSelectedInvoice] =
    useState<boolean>(false);

  // Initalization
  const { height } = useWindowsDimension();
  const [isButtonDisabled] = useState<boolean>(true);
  const [billedPosts, setBilledPosts] = useState<IInvoiceViewModel[]>([]);

  // API
  const { data: billedData } = useGet(rq.getPayedBillingPosts(false), );
  useEffect(() => {
    if (billedData) {
      console.log('BILLED: Billed data fetched:', billedData);
      setBilledPosts(billedData.invoicePost ?? []);
    }
  }, [billedData]);

  const { data: InvoiceData } = useGet(
    rq.getSelectedInvoice(selectedInvoice),
    
  );
  useEffect(() => {
    if (InvoiceData) {
      console.log('BILLED: Selected invoice data', InvoiceData);
      setIsLoadingSelectedInvoice(false);
    }
  }, [InvoiceData]);

  // Helpers for totals
  const getPostTotal = (post: any): number => {
    const qty = Number(post?.quantity ?? 1);
    const unit = Number(post?.retailPrice ?? 0);
    const explicit = Number(post?.totalPrice ?? NaN);
    return Number.isFinite(explicit) ? explicit : qty * unit;
  };

  const posts = useMemo(() => InvoiceData?.invoicePosts ?? [], [InvoiceData]);
  console.log('BILLED: supportPosts total calc:', InvoiceData);
  const tickets = useMemo(() => InvoiceData?.tickets ?? [], [InvoiceData]);
  console.log('BILLED: supportPosts total calc tickets:', tickets);
  const agreementPosts = useMemo(
    () => posts.filter((p: any) => !!p.agreementProduct),
    [posts]
  );
  const companyProductPosts = useMemo(
    () => posts.filter((p: any) => !!p.companyProduct),
    [posts]
  );
  const supportPosts = useMemo(
    () => posts.filter((p: any) => !!p.ticketPost),
    [posts]
  );

  const sumPosts = (arr: any[]) =>
    arr.reduce((acc, p) => acc + getPostTotal(p), 0);

  // Summer alle ticketProducts på tvers av alle tickets
  const ticketProductTotal = useMemo(() => {
    if (!tickets || tickets.length === 0) return 0;

    try {
      return tickets.reduce((accTicket: any, t: any) => {
        const products = t?.ticketProducts ?? [];
        const sumForTicket = products.reduce(
          (accProd: number, p: any) => accProd + getPostTotal(p.product),
          0
        );
        return accTicket + sumForTicket;
      }, 0);
    } catch {
      return 0;
    }
  }, [tickets]);

  // Fallback til invoice-summer hvis post-summer ikke kan beregnes
  const agreementTotal = useMemo(() => {
    const calc = sumPosts(agreementPosts);
    return calc > 0 ? calc : Number(InvoiceData?.agreementInvoiceValue ?? 0);
  }, [agreementPosts, InvoiceData]);

  const companyProductTotal = useMemo(() => {
    const calc = sumPosts(companyProductPosts);
    // Forsøker å bruke beregnet sum først, ellers bruk "otherInvoiceValue" + "investmentCost" som en fornuftig antagelse
    return calc > 0 ? calc : Number(InvoiceData?.investmentCost ?? 0);
  }, [companyProductPosts, InvoiceData]);

  const supportTotal = useMemo(() => {
    const calc = sumPosts(supportPosts);
    return calc > 0 ? calc : Number(InvoiceData?.supportInvoiceValue ?? 0);
  }, [supportPosts, InvoiceData]);

  // Handlers
  const handleInvoiceClick = (invoice: IInvoiceViewModel) => {
    setSelectedInvoice(invoice.id);
    setIsLoadingSelectedInvoice(true);
  };

  const onSubmit = () => {};

  if (billedPosts.length === 0) {
    return null;
  }

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
      {/* LEFT SIDE */}
      <Grid item xs={6} sx={{ pl: 1, m: 0 }}>
        <SearchFilter
          Icon={CreditCardIcon}
          Header={t('Billing.Billed.billedHeader')}
        />
        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 262),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {billedPosts.length === 0 ? (
            <Typography variant='body2' sx={{ opacity: 0.7 }}>
              Ingen fakturaer funnet
            </Typography>
          ) : (
            billedPosts.map((invoice: IInvoiceViewModel, index: number) => {
              const totalBilled =
                (invoice.agreementInvoiceValue ?? 0) +
                (invoice.investmentCost ?? 0) +
                (invoice.otherInvoiceValue ?? 0) +
                (invoice.supportInvoiceValue ?? 0);

              return (
                <Paper
                  key={index}
                  sx={{ p: 1, mb: 1, cursor: 'pointer' }}
                  onClick={() => handleInvoiceClick(invoice)}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {invoice.invoiceNo}
                  </Typography>
                  <Typography variant='body2'>
                    {invoice.company?.name}
                  </Typography>
                  <Typography variant='body2'>
                    {formatValue(totalBilled, 2)}
                  </Typography>
                </Paper>
              );
            })
          )}
        </List>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>

      {/* RIGHT SIDE */}
      <Grid item xs={6} sx={{ pl: 1, m: 0 }}>
        <SearchFilter
          Icon={CreditCardIcon}
          Header={t('Billing.Billed.selectedOrder')}
        />

        {/* Forklaring på accordion-gruppene */}
        <Paper sx={{ p: 2, backgroundColor: 'primary.main' }}>
          <Typography variant='body2'>
            Denne visningen grupperer linjene i tre seksjoner. Åpne en seksjon
            for å se detaljene. Alle seksjoner er lukket som standard.
          </Typography>
        </Paper>

        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 382),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {/* I. Agreement med totalsum */}
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>
                Agreement
              </Typography>
              <Typography>{formatValue(agreementTotal, 2)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {agreementPosts.length === 0 ? (
                <Typography variant='body2' sx={{ opacity: 0.7 }}>
                  Ingen linjer
                </Typography>
              ) : (
                agreementPosts.map((post: any, idx: number) => (
                  <SingleCompanyProductView
                    key={`ag-${idx}`}
                    companyProduct={post.agreementProduct}
                    quantity={post.quantity}
                  />
                ))
              )}
            </AccordionDetails>
          </Accordion>

          {/* II. CompanyProduct med totalsum */}
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>
                CompanyProduct
              </Typography>
              <Typography>{formatValue(companyProductTotal, 2)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {companyProductPosts.length === 0 ? (
                <Typography variant='body2' sx={{ opacity: 0.7 }}>
                  Ingen linjer
                </Typography>
              ) : (
                companyProductPosts.map((post: any, idx: number) => (
                  <SingleCompanyProductView
                    key={`cp-${idx}`}
                    companyProduct={post.companyProduct}
                    quantity={post.quantity}
                  />
                ))
              )}
            </AccordionDetails>
          </Accordion>

          {/* III. Support (ticketPost) med totalsum */}
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>
                Support
              </Typography>
              <Typography>
                {formatValue(supportTotal + ticketProductTotal, 2)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {tickets.length === 0 ? (
                <Typography variant='body2' sx={{ opacity: 0.7 }}>
                  Ingen linjer
                </Typography>
              ) : (
                tickets.map(
                  (ticket: ISimpleTicketBillingViewModel, idx: number) => (
                    <SingleBillingTicketPostView
                      key={ticket.id}
                      ticket={ticket}
                    />
                  )
                )
              )}
            </AccordionDetails>
          </Accordion>
        </List>

        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              p: 0,
              m: 0,
              mb: 2,
              display: 'flex',
              flexDirection: 'row-reverse',
            }}
          >
            <Button
              variant='contained'
              onClick={onSubmit}
              disabled={isButtonDisabled}
            >
              {t('Billing.Billed.updateOrder')}
            </Button>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
